/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

// JS bridge to the native LocationAlwaysPlugin (ios/App/App/). Capacitor's
// stock @capacitor/geolocation only requests WhenInUse on iOS; this plugin
// lets us escalate to Always so Walk Me Home keeps reporting location
// after the screen locks (Batch D P0-18b).
//
// Web / Android no-op: resolve as `unsupported` so the caller can fall
// through to whatever its non-iOS behavior is. The WMH flow already works
// without the escalation on Android.

import { Capacitor, registerPlugin } from "@capacitor/core";

export type LocationAlwaysStatus =
  | "authorized_always"
  | "authorized_when_in_use"
  | "denied"
  | "restricted"
  | "not_determined"
  | "unknown";

export interface LocationAlwaysResult {
  status: LocationAlwaysStatus;
  changed: boolean;
  needsSettings?: boolean;
}

interface LocationAlwaysPluginShape {
  currentStatus(): Promise<{ status: LocationAlwaysStatus }>;
  requestAlways(): Promise<LocationAlwaysResult>;
}

// registerPlugin returns a proxy that throws "not implemented" on non-iOS
// at call time. We guard each call with the platform check below so callers
// never see that error in production.
const LocationAlways = registerPlugin<LocationAlwaysPluginShape>("LocationAlways");

export type LocationAlwaysOutcome =
  | { status: LocationAlwaysStatus; changed: boolean; needsSettings?: boolean }
  | { status: "unsupported"; changed: false };

export async function requestAlwaysLocation(): Promise<LocationAlwaysOutcome> {
  if (Capacitor.getPlatform() !== "ios") {
    return { status: "unsupported", changed: false };
  }
  try {
    const r = await LocationAlways.requestAlways();
    return {
      status: r.status,
      changed: r.changed,
      needsSettings: r.needsSettings,
    };
  } catch (err) {
    console.warn("[locationAlways] requestAlways threw:", err);
    return { status: "unknown", changed: false };
  }
}

export async function currentAlwaysStatus(): Promise<LocationAlwaysStatus | "unsupported"> {
  if (Capacitor.getPlatform() !== "ios") return "unsupported";
  try {
    const r = await LocationAlways.currentStatus();
    return r.status;
  } catch (err) {
    console.warn("[locationAlways] currentStatus threw:", err);
    return "unknown";
  }
}
